export default {
  "slug": "class-a-vs-class-b-circuits",
  "title": "Class A vs Class B (and Class X) Fire Alarm Circuits, Explained",
  "description": "What the NFPA 72 pathway class designations mean — Class B, Class A, and Class X — how redundancy and fault tolerance differ, and when each is used on initiating, notification, and signaling line circuits.",
  "keywords": "class a vs class b fire alarm circuits, class x pathway, NFPA 72 chapter 12 pathways, circuit survivability, SLC class a, fire alarm redundancy",
  "faqs": [
    {
      "q": "What's the core difference between Class A and Class B?",
      "a": "Class B is a simple out-and-back circuit with no redundant return: a single open disables everything downstream of the break. Class A adds a return path so the panel can feed the circuit from both directions, keeping devices operational through a single open and still annunciating the fault."
    },
    {
      "q": "Where does Class X fit in?",
      "a": "Class X provides the redundant pathway of Class A plus short-circuit fault isolation, so it tolerates a single open AND a single short while maintaining operation of unaffected devices. It represents the highest common survivability tier among the standard pathway classes."
    },
    {
      "q": "Is Class A always better?",
      "a": "Class A (or X) improves survivability but costs more wire and installation effort, and doesn't fix a ground fault or protect against physical destruction of a shared raceway. The right class depends on the project's survivability requirements, the AHJ, and the design specification — not a blanket rule."
    }
  ],
  "category": "Circuits",
  "readMins": 6,
  "published": "2026-09-27",
  "updated": "2026-09-27",
  "hero": "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxlbGVjdHJpY2lhbiUyMG11bHRpbWV0ZXIlMjB0ZXN0aW5nJTIwd2lyZXN8ZW58MHx8fHwxNzkwNTI2MDE0fDA&ixlib=rb-4.1.0&q=85",
  "heroAlt": "Technician installing fire alarm circuit wiring",
  "excerpt": "Class A, B, and X describe how much a circuit can survive before it stops working. Here's what each designation actually buys you.",
  "sections": [
    {
      "h": "Pathway classes describe fault tolerance",
      "p": "NFPA 72 Chapter 12 defines pathway 'classes' by how the pathway performs when a fault occurs. The class isn't about the wire type — it's about what still works after a single open, a single short, or a ground. Understanding the class tells you how the system behaves on its worst day, which is exactly when it matters."
    },
    {
      "h": "Class B — simple, no redundant return",
      "p": "A Class B pathway runs from the panel to the last device with end-of-line supervision. It detects a single open or short as a trouble, but a single open disables the devices beyond the break. It's the most common and economical choice where survivability requirements don't demand more."
    },
    {
      "h": "Class A — redundant return survives a single open",
      "p": "A Class A pathway returns to the panel, so the panel can drive the circuit from both ends. A single open is annunciated as a trouble while devices keep operating because they're still fed from the other direction. This costs extra conductors and routing (the return should generally be separated from the outgoing run) but improves reliability."
    },
    {
      "h": "Class X — redundant plus short-circuit isolation",
      "p": "Class X combines the redundant return of Class A with fault isolation for short circuits, so a single open OR a single short still leaves unaffected devices operational. It's the highest survivability tier among the standard classes and is specified where continuity of operation is critical."
    },
    {
      "h": "Choosing a class in practice",
      "p": "Match the class to the survivability the project actually requires: occupant load, building type, mission-critical spaces, and the AHJ's expectations. Remember what a higher class does NOT solve — a ground fault, or a raceway that's physically destroyed, needs separate mitigation such as survivable routing. Always design to the adopted edition and the project specification."
    }
  ],
  "relatedModules": [
    "wiring-circuitry",
    "signal-monitoring",
    "troubleshooting"
  ],
  "relatedGuides": [
    "fire-alarm-wiring-and-circuits",
    "commercial-fire-alarm-troubleshooting"
  ]
};
